import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { RequisitiService } from '../../services/requisiti.service';
import { CommessaModel } from '../../core/models/api/commessa.model';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { UserRoleService } from '../../services/user-role.service';
import { SearchShareService } from '../../services/search-share.service';

@Component({
  selector: 'app-commesse',
  standalone: false,
  templateUrl: './commesse.component.html',
  styleUrl: './commesse.component.scss'
})
export class CommesseComponent implements OnInit, OnDestroy {
  commesse: CommessaModel[] = [];
  filteredCommesse: CommessaModel[] = [];
  loading = false;
  searchText = '';
  
  // Dialog creazione commessa
  showCreateCommessaDialog = false;

  // Inline editing
  editingCommessaId: number | null = null;
  editingCodice: string = '';
  editingDescrizione: string = '';
  savingEdit = false;

  private commesseSub?: Subscription;

  constructor(
    private requisitiService: RequisitiService,
    private logger: NGXLogger,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private userRoleService: UserRoleService,
    private router: Router,
    private searchShareService: SearchShareService
  ) {}

  /**
   * Verifica se l'utente può modificare (non è VIEWER)
   */
  canModify(): boolean {
    return this.userRoleService.canModify();
  }

  /**
   * Verifica se l'utente è VIEWER
   */
  isViewer(): boolean {
    return this.userRoleService.isViewer();
  }

  ngOnInit(): void {
    this.loadCommesse();
  }

  ngOnDestroy(): void {
    this.commesseSub?.unsubscribe();
  }

  /**
   * Carica tutte le commesse
   */
  loadCommesse(): void {
    this.loading = true;
    this.commesseSub = this.requisitiService.getCommesse().subscribe({
      next: (commesse) => {
        this.commesse = commesse;
        this.applyFilter();
        this.loading = false;
        this.logger.info('[COMMESSE] Commesse caricate:', commesse.length);
      },
      error: (error) => {
        this.logger.error('[COMMESSE] Errore nel caricamento delle commesse:', error);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le commesse',
          life: 5000
        });
      }
    });
  }

  /**
   * Applica il filtro di ricerca
   */
  applyFilter(): void {
    if (!this.searchText.trim()) {
      this.filteredCommesse = [...this.commesse];
      return;
    }

    const searchLower = this.searchText.toLowerCase().trim();
    this.filteredCommesse = this.commesse.filter(commessa => {
      return (
        commessa.codice.toLowerCase().includes(searchLower) ||
        commessa.descrizione.toLowerCase().includes(searchLower) ||
        commessa.clienteRagioneSociale.toLowerCase().includes(searchLower) ||
        commessa.id.toString().includes(searchLower)
      );
    });
  }

  /**
   * Gestisce il cambio del testo di ricerca
   */
  onSearchChange(): void {
    this.applyFilter();
  }

  /**
   * Pulisce il filtro di ricerca
   */
  clearSearch(): void {
    this.searchText = '';
    this.applyFilter();
  }

  /**
   * Apre il dialog per creare una nuova commessa
   */
  openCreateCommessaDialog(): void {
    this.showCreateCommessaDialog = true;
  }

  /**
   * Callback chiamato quando una commessa è stata creata
   */
  onCommessaCreated(): void {
    this.logger.info('[COMMESSE] Nuova commessa creata, ricarico la lista');
    this.loadCommesse();
  }

  /**
   * Avvia la modifica inline di una commessa
   */
  startEdit(commessa: CommessaModel): void {
    if (!this.canModify()) return;
    
    this.editingCommessaId = commessa.id;
    this.editingCodice = commessa.codice;
    this.editingDescrizione = commessa.descrizione;
  }

  /**
   * Annulla la modifica inline
   */
  cancelEdit(): void {
    this.editingCommessaId = null;
    this.editingCodice = '';
    this.editingDescrizione = '';
    this.savingEdit = false;
  }

  /**
   * Verifica se il codice modificato è duplicato
   */
  isEditCodiceDuplicate(): boolean {
    if (!this.editingCodice.trim()) return false;

    const codiceNormalized = this.editingCodice.trim().toLowerCase();
    return this.commesse.some(
      c => c.id !== this.editingCommessaId && 
           c.codice.toLowerCase() === codiceNormalized
    );
  }

  /**
   * Salva la modifica della commessa
   */
  async saveEdit(): Promise<void> {
    if (!this.editingCommessaId || !this.editingCodice.trim() || !this.editingDescrizione.trim()) {
      return;
    }

    if (this.isEditCodiceDuplicate()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Validazione',
        detail: 'Esiste già una commessa con questo codice',
        life: 5000
      });
      return;
    }

    // Verifica se il valore è cambiato
    const commessaOriginale = this.commesse.find(c => c.id === this.editingCommessaId);
    if (commessaOriginale && 
        commessaOriginale.codice === this.editingCodice.trim() &&
        commessaOriginale.descrizione === this.editingDescrizione.trim()) {
      this.cancelEdit();
      return;
    }

    this.savingEdit = true;

    try {
      await new Promise<CommessaModel>((resolve, reject) => {
        this.requisitiService
          .updateCommessa(this.editingCommessaId!, {
            codice: this.editingCodice.trim(),
            descrizione: this.editingDescrizione.trim()
          })
          .subscribe({
            next: (commessaAggiornata) => resolve(commessaAggiornata),
            error: (error) => reject(error)
          });
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Commessa aggiornata con successo',
        life: 3000
      });

      this.cancelEdit();
      this.loadCommesse();
    } catch (error) {
      this.logger.error('[COMMESSE] Errore nell\'aggiornamento della commessa:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile aggiornare la commessa. Riprova più tardi.',
        life: 5000
      });
      this.savingEdit = false;
    }
  }

  /**
   * Conferma ed elimina una commessa
   */
  confirmDelete(commessa: CommessaModel): void {
    if (!this.canModify()) return;

    this.confirmationService.confirm({
      message: `Sei sicuro di voler eliminare la commessa <strong>"${commessa.codice} - ${commessa.descrizione}"</strong>?<br><br>Questa operazione non può essere annullata.`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.deleteCommessa(commessa.id);
      }
    });
  }

  /**
   * Elimina una commessa
   */
  private async deleteCommessa(commessaId: number): Promise<void> {
    try {
      await new Promise<void>((resolve, reject) => {
        this.requisitiService.deleteCommessa(commessaId).subscribe({
          next: () => resolve(),
          error: (error) => reject(error)
        });
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Commessa eliminata con successo',
        life: 3000
      });

      this.loadCommesse();
    } catch (error) {
      this.logger.error('[COMMESSE] Errore nell\'eliminazione della commessa:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile eliminare la commessa. Riprova più tardi.',
        life: 5000
      });
    }
  }

  /**
   * Verifica se una commessa è in modalità editing
   */
  isEditing(commessaId: number): boolean {
    return this.editingCommessaId === commessaId;
  }

  /**
   * Naviga alla home con il filtro impostato sulla commessa selezionata
   */
  navigateToHomeWithCommessa(commessa: CommessaModel): void {
    // Non navigare se non ci sono referenze
    if (!commessa.numeroReferenze || commessa.numeroReferenze === 0) {
      return;
    }

    // Crea i parametri di ricerca con la commessa selezionata
    // IMPORTANTE: usa il CODICE della commessa (es: "C001"), non l'ID
    const searchParams = {
      selectedCommessa: commessa.codice
    };

    // Genera la query string
    const queryString = this.searchShareService.serializeSearchParams(searchParams);

    // Costruisce i query params dall'URL generata
    const urlParams = new URLSearchParams(queryString);
    const queryParams: any = {};
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    // Naviga alla home con i parametri
    this.router.navigate(['/'], { queryParams });

    this.logger.debug('[COMMESSE] Navigazione alla home con commessa:', {
      commessaId: commessa.id,
      codice: commessa.codice,
      queryParams
    });
  }

  /**
   * Toggle dello stato aperta/chiusa di una commessa
   */
  async toggleStatoCommessa(commessa: CommessaModel): Promise<void> {
    if (!this.canModify()) return;

    const nuovoStato = !commessa.chiusa;
    const messaggioStato = nuovoStato ? 'chiusa' : 'aperta';

    try {
      await new Promise<CommessaModel>((resolve, reject) => {
        this.requisitiService
          .updateCommessa(commessa.id, { chiusa: nuovoStato } as any)
          .subscribe({
            next: (commessaAggiornata) => resolve(commessaAggiornata),
            error: (error) => reject(error)
          });
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: `Commessa ${messaggioStato} con successo`,
        life: 3000
      });

      this.loadCommesse();
    } catch (error) {
      this.logger.error('[COMMESSE] Errore nel cambio stato della commessa:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile modificare lo stato della commessa. Riprova più tardi.',
        life: 5000
      });
    }
  }
}
